// Collision: skater & puck against the rounded-rectangle rink boundary.
//
// Owns:  the rink boards.
// Reads: tuning.rink, tuning.player.radius.
// Mutates: caller's Vec2 (clampCircleToRink) or player.pos + player.vel
//          (resolvePlayerVsBoards).
// Emits:  no events — board contact is silent from the rules layer's POV.
//         The puck's separate `puck-motion` integrator emits PuckBoardHit
//         for puck-vs-board impacts.
//
// All zero-allocation. The rink is modelled as a rectangle with rounded
// corners (quarter circles inset by `cornerRadius`).

import type { Vec2 } from "../math/Vec2.js";
import { tuning } from "../state/tuning.js";
import type { PlayerState } from "../state/types.js";

/**
 * Clamps a circle of `radius` to the rounded-rectangle rink interior. Mutates
 * `pos` in place. Used by the controlled-puck path (no velocity bounce — the
 * spring re-pulls next tick). Zero-allocation.
 */
export function clampCircleToRink(pos: Vec2, radius: number): void {
  const halfL = tuning.rink.length / 2;
  const halfW = tuning.rink.width / 2;
  const corner = tuning.rink.cornerRadius;

  const x = pos.x;
  const y = pos.y;
  const inLeftEnd = x < -halfL + corner;
  const inRightEnd = x > halfL - corner;
  const inBottomSide = y < -halfW + corner;
  const inTopSide = y > halfW - corner;

  if ((inLeftEnd || inRightEnd) && (inBottomSide || inTopSide)) {
    const cx = inRightEnd ? halfL - corner : -halfL + corner;
    const cy = inTopSide ? halfW - corner : -halfW + corner;
    const dx = x - cx;
    const dy = y - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const maxDist = corner - radius;
    if (dist > maxDist) {
      const ratio = dist === 0 ? 0 : maxDist / dist;
      pos.x = cx + dx * ratio;
      pos.y = cy + dy * ratio;
    }
    return;
  }

  if (x > halfL - radius) pos.x = halfL - radius;
  else if (x < -halfL + radius) pos.x = -halfL + radius;
  if (y > halfW - radius) pos.y = halfW - radius;
  else if (y < -halfW + radius) pos.y = -halfW + radius;
}

/**
 * Resolves the player's position and velocity against the rounded-rectangle
 * rink boundary. Treats the rink interior as the rectangle [-halfL+r, halfL-r] x
 * [-halfW+r, halfW-r], plus quarter-circle corner cutouts. Zero-allocation.
 */
export function resolvePlayerVsBoards(player: PlayerState): void {
  const halfL = tuning.rink.length / 2;
  const halfW = tuning.rink.width / 2;
  const corner = tuning.rink.cornerRadius;
  const r = tuning.player.radius;

  let { x, y } = player.pos;
  let nx = 0;
  let ny = 0;
  let penetrated = false;

  // Determine corner region.
  const inLeftEnd = x < -halfL + corner;
  const inRightEnd = x > halfL - corner;
  const inBottomSide = y < -halfW + corner;
  const inTopSide = y > halfW - corner;

  if ((inLeftEnd || inRightEnd) && (inBottomSide || inTopSide)) {
    // Corner: clamp inside the circle centered at the inset corner.
    const cx = inRightEnd ? halfL - corner : -halfL + corner;
    const cy = inTopSide ? halfW - corner : -halfW + corner;
    const dx = x - cx;
    const dy = y - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const maxDist = corner - r;
    if (dist > maxDist) {
      const ratio = dist === 0 ? 0 : maxDist / dist;
      x = cx + dx * ratio;
      y = cy + dy * ratio;
      penetrated = true;
      nx = -dx / (dist === 0 ? 1 : dist);
      ny = -dy / (dist === 0 ? 1 : dist);
    }
  } else {
    // Straight edge.
    if (x > halfL - r) {
      x = halfL - r;
      nx = -1;
      penetrated = true;
    } else if (x < -halfL + r) {
      x = -halfL + r;
      nx = 1;
      penetrated = true;
    }
    if (y > halfW - r) {
      y = halfW - r;
      ny = -1;
      penetrated = true;
    } else if (y < -halfW + r) {
      y = -halfW + r;
      ny = 1;
      penetrated = true;
    }
  }

  player.pos.x = x;
  player.pos.y = y;
  if (penetrated) {
    // Remove the inward-pointing velocity component (sliding boundary).
    const nMag = Math.sqrt(nx * nx + ny * ny);
    if (nMag > 0) {
      const nUnitX = nx / nMag;
      const nUnitY = ny / nMag;
      const inwardSpeed = player.vel.x * nUnitX + player.vel.y * nUnitY;
      if (inwardSpeed < 0) {
        // velocity is pointing into the wall (opposite of inward normal).
        // Subtract the wall-perpendicular component.
        player.vel.x -= nUnitX * inwardSpeed;
        player.vel.y -= nUnitY * inwardSpeed;
      }
    }
  }
}
